import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Loader2, LogOut, PlusCircle, KeyRound, Eye, EyeOff, FileText, ArrowRight } from "lucide-react";
import { ASSETS } from "@/lib/constants";
import ShelfFolder from "@/components/bdm/ShelfFolder";
import LogActivityModal from "@/components/bdm/LogActivityModal";
import ActivityLogCard from "@/components/bdm/ActivityLogCard";
import BDMLinksCard from "@/components/bdm/BDMLinksCard";
import MeetingCard from "@/components/bdm/MeetingCard";
import SuggestionsCard from "@/components/bdm/SuggestionsCard";
import TodayActionCard from "@/components/bdm/TodayActionCard";
import ThisWeekCard from "@/components/bdm/ThisWeekCard";
import ContactListCard from "@/components/bdm/ContactListCard";
import AnnualCheckCard from "@/components/bdm/AnnualCheckCard";
import { clearGoogleSession } from "@/lib/googleSession";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "grc_bdm_token";

const TIER_RANK = { personal: 0, officer: 1, everyone: 2 };

const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
};

export default function BDMDashboard() {
  const [me, setMe] = useState(null);
  const [shelf, setShelf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logTarget, setLogTarget] = useState(null); // {asset, folder} | {}
  const [plan, setPlan] = useState(null);
  const [planBusy, setPlanBusy] = useState(false);
  const [activityKey, setActivityKey] = useState(0);
  const [loggedByAsset, setLoggedByAsset] = useState({});
  const nav = useNavigate();

  const authHeader = useCallback(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const loadUsage = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bdm/activity`, { headers: authHeader() });
      const map = {};
      for (const it of data.items || []) {
        if (it.asset_id) map[it.asset_id] = (map[it.asset_id] || 0) + 1;
      }
      setLoggedByAsset(map);
    } catch {
      setLoggedByAsset({});
    }
  }, [authHeader]);

  const loadPlan = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bdm/workspace`, { headers: authHeader() });
      setPlan(data);
    } catch {
      setPlan(null);
    }
  }, [authHeader]);

  const completeAction = useCallback(async (action, { log = false } = {}) => {
    setPlanBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/bdm/workspace/done`,
        { key: action.key, contact_id: action.contact?.id || null, log, title: action.title },
        { headers: authHeader() },
      );
      setPlan(data);
      if (data.logged) {
        setActivityKey((v) => v + 1);
        toast.success("Logged to your month-end report");
      } else {
        toast.success("Nice — that's one down");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that.");
    } finally {
      setPlanBusy(false);
    }
  }, [authHeader]);

  useEffect(() => {
    (async () => {
      try {
        const [meRes, shelfRes] = await Promise.all([
          axios.get(`${API}/bdm/auth/me`, { headers: authHeader() }),
          axios.get(`${API}/bdm/shelf`, { headers: authHeader() }),
        ]);
        setMe(meRes.data);
        setShelf(shelfRes.data);
        setLoading(false);
        loadUsage();
        loadPlan();
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        nav("/bdmlogin", { replace: true });
      }
    })();
  }, [authHeader, nav, loadUsage, loadPlan]);

  const recordGrab = useCallback(async (asset, folder) => {
    try {
      await axios.post(
        `${API}/bdm/asset-grab`,
        {
          asset_id: asset.id,
          asset_filename: asset.filename,
          folder_id: folder?.id,
          folder_name: folder?.name,
        },
        { headers: authHeader() },
      );
      setActivityKey((v) => v + 1);
    } catch {
      /* the download already worked — the suggestion is a bonus */
    }
  }, [authHeader]);

  const logout = () => {
    clearGoogleSession();
    localStorage.removeItem(TOKEN_KEY);
    nav("/bdmlogin", { replace: true });
  };

  const sections = useMemo(() => {
    const folders = shelf?.folders || [];
    const current = shelf?.current_month;
    const sorted = [...folders].sort((a, b) => (TIER_RANK[a.tier] ?? 9) - (TIER_RANK[b.tier] ?? 9));
    const thisMonth = sorted.filter((f) => f.drop_month === current);
    const evergreen = sorted.filter((f) => !f.drop_month);
    const earlier = sorted.filter((f) => f.drop_month && f.drop_month !== current);
    const byMonth = {};
    for (const f of earlier) {
      byMonth[f.drop_month] = byMonth[f.drop_month] || [];
      byMonth[f.drop_month].push(f);
    }
    return { thisMonth, evergreen, earlierMonths: Object.keys(byMonth).sort().reverse(), byMonth };
  }, [shelf]);

  if (loading) {
    return (
      <div className="min-h-screen bg-linen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-teal" />
      </div>
    );
  }

  const bdm = me?.bdm || {};
  const mlos = bdm.mlos || [];
  const first = (bdm.name || "there").split(" ")[0];
  const nothingYet =
    sections.thisMonth.length === 0 && sections.evergreen.length === 0 && sections.earlierMonths.length === 0;

  return (
    <div className="min-h-screen bg-linen text-navy antialiased" data-testid="bdm-dashboard-page">
      <header className="bg-white border-b border-linen-border">
        <div className="container-grc h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setLogTarget({})}
              data-testid="bdm-log-anything"
              className="inline-flex items-center gap-1.5 text-sm font-semibold bg-lime text-navy px-4 py-2 rounded-full hover:brightness-95"
            >
              <PlusCircle size={14} /> Log activity
            </button>
            <button
              onClick={logout}
              data-testid="bdm-logout-button"
              className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-navy"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="container-grc py-10 sm:py-14">
        <div className="mb-8 max-w-3xl">
          <p className="text-[11px] uppercase tracking-widest text-teal">Partner dashboard</p>
          <h1 className="mt-1 font-heading font-medium text-3xl sm:text-4xl text-navy">
            Welcome back, {first}.
          </h1>
          <p className="mt-2 text-base sm:text-lg text-ink-500">
            {mlos.length > 0
              ? `Everything here is built for ${mlos.map((m) => m.name).join(" & ")}. `
              : ""}
            You don't have to work out what to do — it's at the top of this page. Do that one thing,
            tap <span className="text-navy font-medium">Sent it</span>, and your month-end report
            writes itself.
          </p>
        </div>

        <div className="space-y-6 mb-10">
          {plan?.journey && !plan.journey.finished && (
            <div
              className="flex flex-wrap items-center gap-3 rounded-2xl bg-white border border-linen-border px-5 py-3.5"
              data-testid="journey-strip"
            >
              <span className="text-[11px] uppercase tracking-widest text-teal font-bold shrink-0">
                First 30 days
              </span>
              <div className="flex-1 min-w-[120px] h-1.5 rounded-full bg-linen overflow-hidden">
                <div
                  className="h-full rounded-full bg-teal"
                  style={{ width: `${Math.round((plan.journey.step * 100) / plan.journey.total)}%` }}
                />
              </div>
              <span className="text-xs text-ink-500 shrink-0">
                Step {plan.journey.step} of {plan.journey.total} · {plan.journey.title}
              </span>
            </div>
          )}

          <TodayActionCard
            action={plan?.today}
            onDone={completeAction}
            onLog={() => setLogTarget({})}
            busy={planBusy}
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ThisWeekCard plan={plan} onDone={completeAction} busy={planBusy} />
            <ContactListCard authHeader={authHeader} onChanged={loadPlan} />
          </div>
          <AnnualCheckCard
            authHeader={authHeader}
            onLogged={() => { setActivityKey((v) => v + 1); loadPlan(); }}
          />
        </div>

        <div className="mb-5 pt-2 border-t border-linen-border" data-testid="bdm-shelf-section">
          <p className="mt-6 text-[11px] uppercase tracking-widest text-ink-400">Marketing shelf</p>
          <h2 className="mt-1 font-heading font-medium text-2xl text-navy">
            Everything else, when you want it
          </h2>
          <p className="mt-1 text-sm text-ink-500 max-w-2xl">
            Campaigns, graphics and templates released to you — plus your links, your log and this
            month's meeting. Nothing here is urgent; the top of the page is.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {nothingYet ? (
              <div className="rounded-3xl bg-white border border-linen-border shadow-soft p-8 text-center" data-testid="bdm-shelf-empty">
                <h2 className="font-heading font-medium text-xl text-navy">Your shelf is being stocked.</h2>
                <p className="mt-2 text-sm text-ink-500">
                  Nothing has been released to you yet. This month's assets land here the moment they're
                  issued — you'll get an email when they do.
                </p>
              </div>
            ) : null}

            {sections.thisMonth.length > 0 && (
              <div className="space-y-4" data-testid="bdm-this-month">
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-teal">
                    This month — {monthLabel(shelf.current_month)}
                  </p>
                  <h2 className="mt-0.5 font-heading font-medium text-2xl text-navy">Your new assets</h2>
                </div>
                {sections.thisMonth.map((f) => (
                  <ShelfFolder
                    key={f.id}
                    folder={f}
                    authHeader={authHeader}
                    loggedByAsset={loggedByAsset}
                    onGrabbed={recordGrab}
                    onLog={(asset, folder) => setLogTarget({ asset, folder })}
                  />
                ))}
              </div>
            )}

            {sections.evergreen.length > 0 && (
              <div className="space-y-4" data-testid="bdm-evergreen">
                <div className="pt-2">
                  <p className="text-[11px] uppercase tracking-widest text-teal">Always available</p>
                  <h2 className="mt-0.5 font-heading font-medium text-2xl text-navy">Your standing shelf</h2>
                </div>
                {sections.evergreen.map((f) => (
                  <ShelfFolder
                    key={f.id}
                    folder={f}
                    authHeader={authHeader}
                    loggedByAsset={loggedByAsset}
                    defaultOpen={false}
                    onGrabbed={recordGrab}
                    onLog={(asset, folder) => setLogTarget({ asset, folder })}
                  />
                ))}
              </div>
            )}

            {sections.earlierMonths.length > 0 && (
              <div className="space-y-4" data-testid="bdm-earlier">
                <div className="pt-2">
                  <p className="text-[11px] uppercase tracking-widest text-teal">Earlier drops</p>
                  <h2 className="mt-0.5 font-heading font-medium text-2xl text-navy">Still usable</h2>
                </div>
                {sections.earlierMonths.map((m) => (
                  <div key={m} className="space-y-3">
                    <p className="text-xs uppercase tracking-widest text-ink-500">{monthLabel(m)}</p>
                    {sections.byMonth[m].map((f) => (
                      <ShelfFolder
                        key={f.id}
                        folder={f}
                        authHeader={authHeader}
                        loggedByAsset={loggedByAsset}
                        defaultOpen={false}
                        onGrabbed={recordGrab}
                    onLog={(asset, folder) => setLogTarget({ asset, folder })}
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          <aside className="space-y-6">
            <SuggestionsCard
              authHeader={authHeader}
              refreshKey={activityKey}
              onLogged={() => { setActivityKey((v) => v + 1); loadUsage(); }}
            />
            <MeetingCard authHeader={authHeader} />
            <div className="bg-white border border-linen-border rounded-3xl shadow-soft p-6" data-testid="bdm-packet-cta-card">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
                <FileText size={13} /> Month end
              </div>
              <h3 className="mt-1 font-heading font-medium text-xl text-navy">Your report, already written</h3>
              <p className="mt-1 text-sm text-ink-500">
                Everything you've logged, turned into the exact wording for each box on the NEXA form —
                with a copy button on every one.
              </p>
              <Link
                to="/bdmdashboard/packet"
                data-testid="bdm-open-packet"
                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold bg-navy text-white px-4 py-2 rounded-full hover:bg-[#173a5f]"
              >
                Open my month-end packet <ArrowRight size={14} />
              </Link>
            </div>
            <ActivityLogCard
              authHeader={authHeader}
              refreshKey={activityKey}
              onLogAnything={() => setLogTarget({})}
            />
            <BDMLinksCard authHeader={authHeader} />
            <ChangePasswordCard authHeader={authHeader} />
          </aside>
        </div>
      </main>

      {logTarget && (
        <LogActivityModal
          asset={logTarget.asset}
          folder={logTarget.folder}
          categories={shelf?.categories || {}}
          channels={shelf?.channels || {}}
          mlos={mlos}
          authHeader={authHeader}
          onClose={() => setLogTarget(null)}
          onSaved={() => {
            setActivityKey((v) => v + 1);
            loadUsage();
          }}
        />
      )}

      <Toaster position="top-center" richColors />
    </div>
  );
}

function ChangePasswordCard({ authHeader }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(
        `${API}/bdm/auth/change-password`,
        { current_password: current, new_password: next },
        { headers: authHeader() },
      );
      toast.success("Password updated");
      setCurrent("");
      setNext("");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not change password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-linen-border rounded-3xl shadow-soft p-6" data-testid="bdm-password-card">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
        <KeyRound size={13} /> Security
      </div>
      <h3 className="mt-1 font-heading font-medium text-xl text-navy">Change my password</h3>
      <form onSubmit={submit} className="mt-4 space-y-3">
        <input
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          placeholder="Current password"
          autoComplete="current-password"
          data-testid="bdm-password-current"
          className="w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none"
        />
        <div className="relative">
          <input
            type={show ? "text" : "password"}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            placeholder="New password (8+ characters)"
            autoComplete="new-password"
            data-testid="bdm-password-new"
            className="w-full rounded-xl border border-linen-border p-2.5 pr-10 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none"
          />
          <button
            type="button"
            onClick={() => setShow((v) => !v)}
            aria-label={show ? "Hide password" : "Show password"}
            className="absolute inset-y-0 right-2 flex items-center px-1 text-ink-400 hover:text-navy"
          >
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <button
          type="submit"
          disabled={busy || !current || next.length < 8}
          data-testid="bdm-password-submit"
          className="w-full inline-flex justify-center items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-full bg-navy text-white hover:bg-[#173a5f] disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Update password
        </button>
      </form>
    </div>
  );
}
